import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Redeem } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { useMutation } from "react-query";
import { useFeedback } from "../../../../providers/feedback/feeedback";
import { verifyOtpForVoucher, verifyVoucherCode, VerifyOtpRequest, VerifyVoucherRequest } from "../../../../services/merchant/merchant_actions";

interface VoucherRedemptionForm {
  voucher_code: string;
  otp: string;
}

// API functions for voucher redemption
const sendVoucherOTP = async (voucherCode: string, merchantId: string) => {
  const requestData: VerifyVoucherRequest = {
    voucher_code: voucherCode,
    merchant_id: merchantId
  };
  
  const response = await verifyVoucherCode(requestData);
  return response.data;
};

const verifyVoucherOTP = async (voucherCode: string, otp: string, merchantId: string) => {
  const requestData: VerifyOtpRequest = {
    voucher_code: voucherCode,
    merchant_id: merchantId,
    otp: parseInt(otp)
  };
  
  const response = await verifyOtpForVoucher(requestData);
  return response.data;
};

export default function MerchantVoucherRedemption() {
  const [otpSent, setOtpSent] = useState(false);
  const [redemptionSuccess, setRedemptionSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const { showSnackbar } = useFeedback();

  const merchantData = JSON.parse(sessionStorage.getItem('merchant_data') || '{}');
  const merchantId = merchantData.merchant_id;

  const { control, handleSubmit, watch, formState: { errors }, reset } = useForm<VoucherRedemptionForm>({
    defaultValues: { voucher_code: '', otp: '' }
  });

  const voucherCode = watch('voucher_code');

  const sendOTPMutation = useMutation({
    mutationFn: (voucherCode: string) => sendVoucherOTP(voucherCode, merchantId),
    onSuccess: () => {
      setOtpSent(true);
      setResendCooldown(30);
      showSnackbar("OTP sent successfully");
      
      const timer = setInterval(() => {
        setResendCooldown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    onError: (error: any) => {
        // setOtpSent(true)
      if (error?.response?.data?.error?.message) {
        showSnackbar(error.response.data.error.message);
      } else if (error?.message) {
        showSnackbar(error.message);
      } else {
        showSnackbar("Failed to send OTP. Please try again.");
      }
    }
  });

  const verifyOTPMutation = useMutation({
    mutationFn: ({ voucherCode, otp }: { voucherCode: string; otp: string }) => 
      verifyVoucherOTP(voucherCode, otp, merchantId),
    onSuccess: () => {
      setRedemptionSuccess(true);
      showSnackbar("Voucher redeemed successfully!");
    },
    onError: (error: any) => {
      // Handle specific error cases
      if (error?.response?.data?.error?.message === "Voucher already redeemed") {
        showSnackbar("This voucher has already been redeemed and cannot be used again.");
      } else if (error?.response?.data?.error?.message) {
        showSnackbar(error.response.data.error.message);
      } else if (error?.message) {
        showSnackbar(error.message);
      } else {
        showSnackbar("An error occurred while redeeming the voucher. Please try again.");
      }
    }
  });

  const handleSendOTP = () => {
    if (!voucherCode.trim()) {
      showSnackbar("Please enter a voucher code first");
      return;
    }
    sendOTPMutation.mutate(voucherCode);
  };

  const handleResendOTP = () => {
    if (voucherCode.trim()) {
      sendOTPMutation.mutate(voucherCode);
    }
  };

  const onSubmit = (data: VoucherRedemptionForm) => {
    verifyOTPMutation.mutate({ voucherCode: data.voucher_code, otp: data.otp });
  };

  const handleNewRedemption = () => {
    setOtpSent(false);
    setRedemptionSuccess(false);
    setResendCooldown(0);
    reset();
  };

  if (redemptionSuccess) {
    return (
      <Box>
        <Typography variant="h4" color="#91278F" fontWeight="600" gutterBottom>
          Voucher Redemption
        </Typography>
        
        <Card sx={{ maxWidth: 600, mx: 'auto' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Redeem sx={{ fontSize: 64, color: '#4CAF50', mb: 2 }} />
            <Typography variant="h5" color="#4CAF50" fontWeight="600" gutterBottom>
              Redemption Successful!
            </Typography>
            <Alert severity="success" sx={{ mb: 3 }}>
              The voucher has been redeemed successfully.
            </Alert>
            <Button
              variant="contained"
              onClick={handleNewRedemption}
              sx={{ backgroundColor: "#91278F", '&:hover': { backgroundColor: "#7a1f7a" } }}
            >
              Redeem Another Voucher
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" color="#91278F" fontWeight="600" gutterBottom>
        Voucher Redemption
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Enter the voucher code to process redemption
      </Typography>

      <Card sx={{ maxWidth: 600, mx: 'auto' }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" mb={3}>
            <Redeem sx={{ mr: 2, color: '#91278F', fontSize: 32 }} />
            <Box>
              <Typography variant="h6" color="#91278F">
                Redeem Voucher
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {otpSent ? "Enter OTP sent to customer" : "Enter voucher code to send OTP"}
              </Typography>
            </Box>
          </Box>

          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Controller
              name="voucher_code"
              control={control}
              rules={{ required: "Voucher code is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Voucher Code"
                  placeholder="Enter voucher code"
                  error={!!errors.voucher_code}
                  helperText={errors.voucher_code?.message}
                  disabled={otpSent}
                  sx={{ mb: 3 }}
                />
              )}
            />

            {otpSent && (
              <>
                <Controller
                  name="otp"
                  control={control}
                  rules={{ 
                    required: 'OTP is required',
                    pattern: { value: /^\d{4}$/, message: 'Please enter a valid 4-digit OTP' }
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Enter OTP"
                      placeholder="Enter 4-digit OTP"
                      error={!!errors.otp}
                      helperText={errors.otp?.message || "OTP sent to customer's mobile number"}
                      inputProps={{ maxLength: 4 }}
                      sx={{ mb: 3 }}
                    />
                  )}
                />

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Button
                    variant="outlined"
                    onClick={handleResendOTP}
                    disabled={resendCooldown > 0 || sendOTPMutation.isLoading}
                    sx={{ borderColor: "#91278F", color: "#91278F" }}
                  >
                    {sendOTPMutation.isLoading ? (
                      <CircularProgress size={20} />
                    ) : resendCooldown > 0 ? (
                      `Resend OTP in ${resendCooldown}s`
                    ) : (
                      'Resend OTP'
                    )}
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handleSubmit(onSubmit)}
                    disabled={verifyOTPMutation.isLoading}
                    sx={{ backgroundColor: "#91278F", '&:hover': { backgroundColor: "#7a1f7a" } }}
                  >
                    {verifyOTPMutation.isLoading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      'Verify & Redeem'
                    )}
                  </Button>
                </Box>
              </>
            )}

            {!otpSent && (
              <Button
                variant="contained"
                fullWidth
                onClick={handleSendOTP}
                disabled={!voucherCode.trim() || sendOTPMutation.isLoading}
                sx={{ backgroundColor: "#91278F", '&:hover': { backgroundColor: "#7a1f7a" } }}
              >
                {sendOTPMutation.isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Send OTP'
                )}
              </Button>
            )}
          </Box>

          <Paper sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5' }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> An OTP will be sent to the customer's mobile number for verification.
            </Typography>
          </Paper>

        </CardContent>
      </Card>
    </Box>
  );
}